import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Panel } from "@/components/Panel";
import { toast } from "sonner";
import BookCallDialog from "@/components/BookCallDialog";

// Phase 25.13k — in-app billing tab mirrors the public /pricing 4-tier ladder.
// Starter $299 · Scale $1,499 (MOST POPULAR) · Professional $3,499 · Enterprise Custom.
// Starter & Scale → Stripe Checkout (self-serve). Professional & Enterprise → Book-a-demo.

const TIERS = [
  {
    id: "starter",
    name: "STARTER",
    price: "$299",
    cadence: "/ month",
    feats: [
      "POS Live · Daily DARA briefing",
      "1 workspace · 1 connected POS",
      "CSV upload + data profiling",
      "Email support · 1 business-day reply",
    ],
    cta: "SUBSCRIBE",
    testId: "plan-starter-subscribe",
    selfServe: true,
  },
  {
    id: "scale",
    name: "SCALE",
    price: "$1,499",
    cadence: "/ month",
    featured: true,
    feats: [
      "Everything in Starter",
      "Xero · MYOB · Square multi-source",
      "Hedge Ledger · Cash-Flow Forecast",
      "Industry playbooks · Brand Kit",
      "1 Expert Network session / month",
    ],
    cta: "SUBSCRIBE",
    testId: "plan-scale-subscribe",
    selfServe: true,
  },
  {
    id: "professional",
    name: "PROFESSIONAL",
    price: "$3,499",
    cadence: "/ month",
    feats: [
      "Everything in Scale",
      "Unlimited workspaces",
      "11 jurisdiction rulebooks",
      "Dedicated Ops Desk success engineer",
      "Quarterly Expert deep-dive (incl.)",
    ],
    cta: "BOOK A DEMO",
    testId: "plan-professional-cta",
    selfServe: false,
    interest: "professional",
  },
  {
    id: "enterprise",
    name: "ENTERPRISE",
    price: "Custom",
    cadence: "",
    feats: [
      "Everything in Professional",
      "Air-gapped deployment option",
      "SCADA · OPC UA · MODBUS adapters",
      "SOC 2 path · custom DPA · SSO",
      "White-label / re-seller options",
    ],
    cta: "BOOK A DEMO",
    testId: "plan-enterprise-cta",
    selfServe: false,
    interest: "enterprise",
  },
];

export default function BillingTab() {
  const [sub, setSub] = useState(null);
  const [nudge, setNudge] = useState(null);
  const [starting, setStarting] = useState(null);  // package_id | null
  const [callDialog, setCallDialog] = useState({ open: false, interest: "enterprise" });

  useEffect(() => {
    api.get("/billing/subscription").then(r => setSub(r.data));
    api.get("/billing/upgrade-nudge").then(r => setNudge(r.data)).catch(() => setNudge(null));
  }, []);

  const startCheckout = async (packageId) => {
    setStarting(packageId);
    try {
      const { data } = await api.post("/billing/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setStarting(null);
    }
  };

  const openCall = (interest) => setCallDialog({ open: true, interest });

  return (
    <div className="space-y-6" data-testid="billing-tab">
      <Panel title="// CURRENT PLAN">
        <div className="p-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">PLAN</div>
            <div className="font-heading font-black text-3xl uppercase mt-1">{(sub?.plan || "trial")}</div>
            <div className="font-mono text-[11px] text-zinc-500 mt-1">
              Status: <span className={sub?.status === "active" ? "text-[#00E676]" : "text-[#FFB000]"}>{(sub?.status || "trial").toUpperCase()}</span>
            </div>
          </div>
          {sub?.started_at && (
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">STARTED</div>
              <div className="font-mono text-sm mt-1">{new Date(sub.started_at).toLocaleDateString()}</div>
            </div>
          )}
        </div>
      </Panel>

      {nudge?.show && (
        <UpgradeNudge
          nudge={nudge}
          onUpgrade={() => nudge.next_tier === "scale" ? startCheckout("scale") : openCall("professional")}
          loading={starting === nudge.next_tier}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="billing-tier-grid">
        {TIERS.map((t) => (
          <PlanCard
            key={t.id}
            tier={t}
            loading={starting === t.id}
            active={sub?.plan === t.id && sub?.status === "active"}
            onClick={() => t.selfServe ? startCheckout(t.id) : openCall(t.interest)}
          />
        ))}
      </div>

      <BookCallDialog
        open={callDialog.open}
        defaultInterest={callDialog.interest}
        onClose={() => setCallDialog({ open: false, interest: "enterprise" })}
      />
    </div>
  );
}

function PlanCard({ tier, onClick, loading, active }) {
  const { name, price, cadence, feats, cta, featured, testId } = tier;
  return (
    <div className={`panel p-6 flex flex-col ${featured ? "border-[#FFB000]/40" : ""} ${active ? "ring-1 ring-[#00E676]/40" : ""}`} data-testid={`billing-card-${tier.id}`}>
      {featured && <div className="font-mono text-[9px] uppercase tracking-widest text-[#FFB000] mb-3">◆ MOST POPULAR</div>}
      {active && <div className="font-mono text-[9px] uppercase tracking-widest text-[#00E676] mb-3">✓ CURRENT PLAN</div>}
      <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">{name}</div>
      <div className="flex items-baseline gap-2 mb-4">
        <span className="font-mono text-3xl font-medium">{price}</span>
        <span className="font-mono text-[10px] text-zinc-500">{cadence}</span>
      </div>
      <ul className="space-y-2 mb-6 flex-1">
        {feats.map(f => (
          <li key={f} className="font-mono text-[11px] text-zinc-300 flex gap-2">
            <span className="text-[#FFB000]">▸</span>{f}
          </li>
        ))}
      </ul>
      <button
        onClick={onClick}
        disabled={loading || active}
        data-testid={testId}
        className={`w-full py-2.5 font-mono text-[11px] uppercase tracking-wider transition-colors ${
          featured ? "bg-[#FFB000] text-black hover:bg-[#E59E00]" : "border border-zinc-700 hover:bg-zinc-900"
        } disabled:opacity-50`}
      >
        {loading ? "REDIRECTING…" : active ? "ACTIVE" : cta}
      </button>
    </div>
  );
}

// Phase 25.13l — Usage-aware upgrade nudge panel.
// Surfaces the next-tier features the user is most likely to actually use,
// based on real DB signals (data sources, integrations, insights, members).
function UpgradeNudge({ nudge, onUpgrade, loading }) {
  const nextLabel = (nudge.next_tier || "scale").toUpperCase();
  const cta = nudge.next_tier === "scale" ? "UPGRADE TO SCALE" : "TALK TO US";
  return (
    <div
      className="border border-[#FFB000]/30 bg-[#0a0700] p-5 sm:p-6 relative overflow-hidden"
      data-testid="billing-upgrade-nudge"
    >
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-[#FFB000]/5 to-transparent pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
          <span className="w-1.5 h-1.5 bg-[#FFB000] rounded-full pulse-dot" />
          // PERSONALISED · BASED ON YOUR USAGE THIS WEEK
        </div>
        <h3 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-2">
          You'd unlock these {nudge.signals.length} things on <span className="text-[#FFB000]">{nextLabel}</span>.
        </h3>
        <p className="font-mono text-[11px] text-zinc-400 mb-5">
          {nextLabel} · {nudge.next_tier_price} · 14-day free upgrade available
        </p>

        <ul className="space-y-2.5 mb-6">
          {nudge.signals.map((s, idx) => (
            <li key={idx} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_2fr] gap-2 sm:gap-4 items-start sm:items-center" data-testid={`nudge-signal-${idx}`}>
              <div className="font-mono text-[11px] text-zinc-400 flex items-start gap-2">
                <span className="text-[#00E676] mt-[3px]">▸</span>
                <span>{s.label}</span>
              </div>
              <div className="hidden sm:block font-mono text-[10px] text-zinc-700">→</div>
              <div className="font-mono text-[11px] text-white">
                <span className="text-[#FFB000]">UNLOCKS</span> · {s.unlock}
              </div>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={onUpgrade}
            disabled={loading}
            data-testid="nudge-upgrade-cta"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-wider px-5 py-2.5 hover:bg-[#E59E00] transition-colors disabled:opacity-50"
          >
            {loading ? "REDIRECTING…" : cta}
          </button>
          <span className="font-mono text-[10px] text-zinc-600 uppercase tracking-widest">
            Cancel anytime · Pro-rated billing
          </span>
        </div>
      </div>
    </div>
  );
}
